import { useRef } from 'react';
import {
  SignatureFieldOverlay,
  type SignatureField,
} from '@/modules/templates/components/review/SignatureFieldOverlay';
import type { Signatory } from '@/modules/templates/components/review/utils';
import type { DocxPageGeometry } from './useDocxPageGeometry';

interface DocxPageFieldHostProps {
  page: DocxPageGeometry;
  appliedScale: number;
  fields: SignatureField[];
  signatories: Signatory[];
  selectedField: string | null;
  onSelectField: (id: string | null) => void;
  onFieldsChange: (fields: SignatureField[]) => void;
}

function DocxPageFieldHost({
  page,
  appliedScale,
  fields,
  signatories,
  selectedField,
  onSelectField,
  onFieldsChange,
}: DocxPageFieldHostProps) {
  const hostRef = useRef<HTMLDivElement>(null);

  return (
    <div
      ref={hostRef}
      style={{
        position: 'absolute',
        left: page.left,
        top: page.top,
        width: page.width,
        height: page.height,
        // The host itself must not swallow clicks — hover and click-to-edit
        // belong to the document underneath. Field boxes re-enable pointer
        // events on themselves.
        pointerEvents: 'none',
      }}
    >
      <SignatureFieldOverlay
        fields={fields}
        signatories={signatories}
        currentPage={page.pageNumber}
        scale={appliedScale}
        // Pass the ref, not the element: the overlay resolves it lazily inside
        // its handlers, which avoids the first-render-null dance.
        pageRef={hostRef}
        // Base page width at scale 1 — the overlay multiplies back up by `scale`.
        pageWidth={page.width / (appliedScale || 1)}
        pageWidthPt={page.widthPt}
        pageHeightPt={page.heightPt}
        // The host IS the page box here, so there is no page chrome to
        // compensate for — react-pdf's default nudge would just sit fields high.
        topNudgePx={0}
        onFieldsChange={onFieldsChange}
        selectedField={selectedField}
        onSelectField={onSelectField}
        onPageClick={() => undefined}
      />
    </div>
  );
}

interface DocxPageOverlayLayerProps {
  pages: DocxPageGeometry[];
  appliedScale: number;
  fields: SignatureField[];
  signatories: Signatory[];
  selectedField: string | null;
  onSelectField: (id: string | null) => void;
  onFieldsChange: (fields: SignatureField[]) => void;
}

/**
 * Draggable signature fields over a rendered .docx.
 *
 * This layer must be a sibling of the `transform: scale()` wrapper, never a
 * child of it. `SignatureFieldOverlay` positions fields in CSS pixels but reads
 * drag deltas from `getBoundingClientRect()` and `clientX`, which are *screen*
 * pixels. Those two agree only when the host is outside any CSS transform —
 * mounting inside the scaled wrapper would leave rendering correct while
 * inflating every drag delta by the zoom factor.
 *
 * So each page gets an absolutely-positioned host at the section's scaled
 * offset, and the overlay is handed the real `appliedScale`.
 */
export function DocxPageOverlayLayer({
  pages,
  appliedScale,
  fields,
  signatories,
  selectedField,
  onSelectField,
  onFieldsChange,
}: DocxPageOverlayLayerProps) {
  if (pages.length === 0) return null;

  return (
    <div className="absolute inset-0" style={{ pointerEvents: 'none', zIndex: 11 }}>
      {pages.map((page) => (
        <DocxPageFieldHost
          key={page.pageNumber}
          page={page}
          appliedScale={appliedScale}
          fields={fields}
          signatories={signatories}
          selectedField={selectedField}
          onSelectField={onSelectField}
          onFieldsChange={onFieldsChange}
        />
      ))}
    </div>
  );
}
